import { useMemo } from 'react';

import { getLanguage } from 'api/utils/languageManagment';

import { Container, DayButton, DayLabel, DayNumber } from './styled';

type CalendarLayout = 'centered' | 'today-first';

interface CalendarProps {
    selectedDate: Date;
    onDateChange: (date: Date) => void;
    layout?: CalendarLayout;
}

const TOTAL_DAYS = 7;
const MIDDLE_INDEX = Math.floor(TOTAL_DAYS / 2);

const addDays = (anchor: Date, amount: number): Date => {
    const day = new Date(anchor);

    day.setDate(anchor.getDate() + amount);
    day.setHours(0, 0, 0, 0);

    return day;
};

const buildRange = (anchor: Date, startOffset: number): Date[] =>
    Array.from({ length: TOTAL_DAYS }, (_, index) => addDays(anchor, startOffset + index));

const LAYOUTS: Record<CalendarLayout, (selectedDate: Date) => Date[]> = {
    centered: (selectedDate) => buildRange(selectedDate, -MIDDLE_INDEX),
    'today-first': () => buildRange(new Date(), 0),
};

const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
const isToday = (date: Date) => isSameDay(date, new Date());

export const Calendar = ({ selectedDate, onDateChange, layout = 'centered' }: CalendarProps) => {
    const days = useMemo(() => LAYOUTS[layout](selectedDate), [layout, selectedDate]);

    return (
        <Container>
            {days.map((day) => (
                <DayButton
                    key={day.toISOString()}
                    $isToday={isToday(day)}
                    $isSelected={isSameDay(day, selectedDate)}
                    onClick={() => onDateChange(day)}
                    disabled={isSameDay(day, selectedDate)}
                >
                    <DayLabel>{day.toLocaleDateString(getLanguage(), { weekday: 'short' })}</DayLabel>
                    <DayNumber>{day.getDate()}</DayNumber>
                </DayButton>
            ))}
        </Container>
    );
};
